import { apiClient } from './client';
import { 
  UserPreferences, 
  UpdatePreferencesRequest, 
  PreferencesResponse,
  Watchlist,
  WatchlistResponse,
  CreateWatchlistRequest,
  UpdateWatchlistRequest,
  AddToWatchlistRequest,
  WatchlistInstrument,
  DEFAULT_PREFERENCES
} from '../../types/preferences';

export class PreferencesService {
  private apiClient = apiClient;

  /**
   * Get user preferences
   */
  async getUserPreferences(): Promise<UserPreferences> {
    try {
      const response = await this.apiClient.get<PreferencesResponse>('/v1/preferences');
      return response.preferences;
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      // Return default preferences if API call fails
      return {
        userId: 'default',
        ...DEFAULT_PREFERENCES,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    }
  }

  /**
   * Update user preferences
   */
  async updatePreferences(updates: UpdatePreferencesRequest): Promise<UserPreferences> {
    try {
      const response = await this.apiClient.put<PreferencesResponse>('/v1/preferences', updates);
      return response.preferences;
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  }

  /**
   * Reset preferences to defaults
   */
  async resetPreferences(): Promise<UserPreferences> {
    try {
      const response = await this.apiClient.post<PreferencesResponse>('/v1/preferences/reset', {});
      return response.preferences;
    } catch (error) {
      console.error('Error resetting preferences:', error);
      throw error;
    }
  }

  /**
   * Get all user watchlists
   */
  async getWatchlists(): Promise<Watchlist[]> {
    try {
      const response = await this.apiClient.get<WatchlistResponse>('/v1/watchlists');
      return response.watchlists;
    } catch (error) {
      console.error('Error fetching watchlists:', error);
      // Return empty array if API call fails
      return [];
    }
  }

  /**
   * Get specific watchlist by ID
   */
  async getWatchlist(watchlistId: string): Promise<Watchlist> {
    try {
      const watchlist = await this.apiClient.get<Watchlist>(`/v1/watchlists/${watchlistId}`);
      return watchlist;
    } catch (error) {
      console.error(`Error fetching watchlist ${watchlistId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new watchlist
   */
  async createWatchlist(watchlistData: CreateWatchlistRequest): Promise<Watchlist> {
    try {
      const watchlist = await this.apiClient.post<Watchlist>('/v1/watchlists', watchlistData);
      return watchlist;
    } catch (error) {
      console.error('Error creating watchlist:', error);
      throw error;
    }
  }

  /**
   * Update watchlist details
   */
  async updateWatchlist(watchlistId: string, updates: UpdateWatchlistRequest): Promise<Watchlist> {
    try {
      const watchlist = await this.apiClient.put<Watchlist>(`/v1/watchlists/${watchlistId}`, updates);
      return watchlist;
    } catch (error) {
      console.error(`Error updating watchlist ${watchlistId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a watchlist
   */
  async deleteWatchlist(watchlistId: string): Promise<{ deleted: boolean; id: string }> {
    try {
      const result = await this.apiClient.delete<{ deleted: boolean; id: string }>(`/v1/watchlists/${watchlistId}`);
      return result;
    } catch (error) {
      console.error(`Error deleting watchlist ${watchlistId}:`, error);
      throw error;
    }
  }

  /**
   * Add instrument to watchlist
   */
  async addToWatchlist(watchlistId: string, instrumentData: AddToWatchlistRequest): Promise<WatchlistInstrument> {
    try {
      const instrument = await this.apiClient.post<WatchlistInstrument>(
        `/v1/watchlists/${watchlistId}/instruments`, 
        instrumentData
      );
      return instrument;
    } catch (error) {
      console.error(`Error adding instrument to watchlist ${watchlistId}:`, error);
      throw error;
    }
  }

  /**
   * Remove instrument from watchlist
   */
  async removeFromWatchlist(watchlistId: string, instrumentId: string): Promise<{ removed: boolean; instrumentId: string }> {
    try {
      const result = await this.apiClient.delete<{ removed: boolean; instrumentId: string }>(
        `/v1/watchlists/${watchlistId}/instruments/${instrumentId}`
      );
      return result;
    } catch (error) {
      console.error(`Error removing instrument ${instrumentId} from watchlist ${watchlistId}:`, error);
      throw error;
    }
  }

  /**
   * Set default watchlist
   */
  async setDefaultWatchlist(watchlistId: string): Promise<{ success: boolean; defaultWatchlistId: string }> {
    try {
      const result = await this.apiClient.put<{ success: boolean; defaultWatchlistId: string }>(
        `/v1/watchlists/${watchlistId}/default`, 
        {}
      );
      return result;
    } catch (error) {
      console.error(`Error setting default watchlist ${watchlistId}:`, error);
      throw error;
    }
  }

  /**
   * Get default watchlist
   */
  async getDefaultWatchlist(): Promise<Watchlist | null> {
    try {
      const watchlists = await this.getWatchlists();
      return watchlists.find(w => w.isDefault) || null;
    } catch (error) {
      console.error('Error fetching default watchlist:', error);
      return null;
    }
  }

  /**
   * Search instruments for watchlist addition
   */
  async searchInstruments(query: string): Promise<{ instrumentId: string; symbol: string; name: string }[]> {
    try {
      // This might use the existing instrument service or a separate search endpoint
      const instruments = await this.apiClient.get<{ instruments: any[] }>(`/v1/instruments/search?q=${encodeURIComponent(query)}`);
      return instruments.instruments.map(inst => ({
        instrumentId: inst.id,
        symbol: inst.symbol,
        name: inst.name
      }));
    } catch (error) {
      console.error('Error searching instruments:', error);
      return [];
    }
  }

  /**
   * Export watchlist data (for backup/sharing)
   */
  async exportWatchlist(watchlistId: string): Promise<string> {
    try {
      const watchlist = await this.getWatchlist(watchlistId);
      const exportData = {
        name: watchlist.name,
        description: watchlist.description,
        instruments: watchlist.instruments.map(inst => ({
          symbol: inst.symbol,
          name: inst.name,
          notes: inst.notes
        })),
        exportedAt: new Date().toISOString()
      };
      return JSON.stringify(exportData, null, 2);
    } catch (error) {
      console.error(`Error exporting watchlist ${watchlistId}:`, error);
      throw error;
    }
  }

  /**
   * Import watchlist from JSON data
   */
  async importWatchlist(name: string, jsonData: string): Promise<Watchlist> {
    try {
      const importData = JSON.parse(jsonData);
      const createRequest: CreateWatchlistRequest = {
        name: name,
        description: importData.description || `Imported watchlist - ${new Date().toLocaleDateString()}`,
        instruments: importData.instruments?.map((inst: any) => inst.instrumentId || inst.symbol) || []
      };
      return this.createWatchlist(createRequest);
    } catch (error) {
      console.error('Error importing watchlist:', error);
      throw error;
    }
  }

  /**
   * Get preferences section (trading, ui, chart, etc.)
   */
  async getPreferencesSection<T extends keyof Omit<UserPreferences, 'userId' | 'createdAt' | 'updatedAt'>>(section: T): Promise<UserPreferences[T]> {
    try {
      const preferences = await this.getUserPreferences();
      return preferences[section];
    } catch (error) {
      console.error(`Error fetching ${section} preferences:`, error);
      return DEFAULT_PREFERENCES[section] as UserPreferences[T];
    }
  }

  /**
   * Update specific preferences section
   */
  async updatePreferencesSection<T extends keyof UpdatePreferencesRequest>(
    section: T, 
    updates: UpdatePreferencesRequest[T]
  ): Promise<UserPreferences> {
    try {
      const updateRequest: UpdatePreferencesRequest = {
        [section]: updates
      };
      return this.updatePreferences(updateRequest);
    } catch (error) {
      console.error(`Error updating ${section} preferences:`, error);
      throw error;
    }
  }

  /**
   * Format watchlist for display
   */
  formatWatchlistForUI(watchlist: Watchlist) {
    return {
      ...watchlist,
      formattedCreatedAt: new Date(watchlist.createdAt).toLocaleDateString(),
      formattedUpdatedAt: new Date(watchlist.updatedAt).toLocaleDateString(),
      instrumentCount: watchlist.instruments.length,
      hasAlerts: watchlist.instruments.some(inst => inst.alertPrice !== undefined)
    };
  }

  /**
   * Validate preferences data
   */
  validatePreferences(preferences: Partial<UserPreferences>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate trading preferences
    if (preferences.trading) {
      if (preferences.trading.defaultStake !== undefined && preferences.trading.defaultStake <= 0) {
        errors.push('Default stake must be greater than 0');
      }
      if (preferences.trading.riskLevel && !['conservative', 'moderate', 'aggressive'].includes(preferences.trading.riskLevel)) {
        errors.push('Invalid risk level');
      }
    }

    // Validate UI preferences
    if (preferences.ui) {
      if (preferences.ui.theme && !['light', 'dark', 'auto'].includes(preferences.ui.theme)) {
        errors.push('Invalid theme');
      }
      if (preferences.ui.dateFormat && !['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'].includes(preferences.ui.dateFormat)) {
        errors.push('Invalid date format');
      }
    }

    // Validate chart preferences
    if (preferences.chart) {
      if (preferences.chart.defaultType && !['candlestick', 'line'].includes(preferences.chart.defaultType)) {
        errors.push('Invalid chart type');
      }
      if (preferences.chart.defaultTimeframe && !['1s', '1m', '5m', '15m', '1h', '1d'].includes(preferences.chart.defaultTimeframe)) {
        errors.push('Invalid timeframe');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Export singleton instance
export const preferencesService = new PreferencesService();
